import React from 'react';
import Navbar from '../components/Navbar';
import './DMIPPage.css';

const DMIPPage = () => {
  return (
    <div className="dmip-page">
      <Navbar title="Data Management Intelligence Platform" />

      {/* ── Hero ── */}
      <section className="dmip-hero">
        <div className="container">
          <p className="dmip-hero-eyebrow">The Framework is Now a Platform.</p>
          <h1>Data Management Intelligence Platform</h1>
          <p className="dmip-hero-sub">
            Structured discovery. Knowledge graphs. AI-generated plans.
            Built on D-DMF. Running on real engagements.
          </p>
          <div className="dmip-hero-buttons">
            <a href="mailto:krupesh@dmip.app?subject=DMIP Access Request" className="btn-primary">Request Access</a>
            <a href="mailto:krupesh@dmip.app?subject=DMIP Partnership Enquiry" className="btn-secondary">Partner With Us</a>
          </div>
        </div>
      </section>

      {/* ── Story ── */}
      <section className="dmip-story">
        <div className="container">
          <p>
            The Desai Data Migration Framework (D-DMF) was published to give data migration professionals a structured
            way to approach legacy system decommissioning. DMIP is what happens when that
            framework meets real-world scale. Take any system — finance, property, records,
            assets — assume you are retiring it today, and run it through structured discovery.
            Every answer builds intelligence. Every engagement generates a plan.
          </p>
          <p>
            DMIP handles multiple legacy systems simultaneously, connects them through a live
            knowledge graph, and generates professional Migration, Governance, and Data Quality
            plans from structured discovery. AI features are optional — clients choose whether
            to enable them. The platform works without AI. It works better with it.
          </p>
          <p className="dmip-story-punch">
            The book preaches it. The platform delivers it. Legacy system retirement is your
            golden opportunity to uplift data governance — DMIP makes that opportunity
            impossible to miss.
          </p>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="dmip-features-section">
        <div className="container">
          <div className="dmip-section-header">
            <h2>What DMIP Does</h2>
            <p>
              Every component of D-DMF, now running as a live platform — capturing
              discovery data, building knowledge graphs, and generating delivery artefacts.
            </p>
          </div>

          <div className="dmip-feature-cards">
            {/* Card 1 */}
            <div className="dmip-feature-card">
              <div className="dmip-feature-icon">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="6" cy="12" r="2"/>
                  <circle cx="18" cy="6" r="2"/>
                  <circle cx="18" cy="18" r="2"/>
                  <path strokeLinecap="round" d="M8 11.5l8-4M8 12.5l8 4"/>
                </svg>
              </div>
              <h3>Structured Discovery</h3>
              <p>
                10-component D-DMF engine captures every legacy system — scope,
                segments, risks, integrations, data quality, and governance — in one
                structured engagement.
              </p>
            </div>

            {/* Card 2 */}
            <div className="dmip-feature-card">
              <div className="dmip-feature-icon">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3l4 4-4 4M12 3l4 4-4 4M19 12c0 3.866-3.134 7-7 7s-7-3.134-7-7"/>
                </svg>
              </div>
              <h3>Knowledge Graph</h3>
              <p>
                Every answer builds a live knowledge graph connecting systems,
                people, risks, and integrations. Cross-system intelligence surfaces
                automatically.
              </p>
            </div>

            {/* Card 3 */}
            <div className="dmip-feature-card">
              <div className="dmip-feature-icon">
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <h3>AI Plan Generation</h3>
              <p>
                Migration Plans, Governance Plans, and Data Quality Plans
                generated from discovery data. Section by section. Export to Word.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Showcase ── */}
      <section className="dmip-showcase">
        <div className="container">

          {/* Row 1 — image left, text right */}
          <div className="dmip-showcase-row">
            <div className="dmip-showcase-image">
              <img src="/dmip_graph.png" alt="DMIP Knowledge Graph" />
            </div>
            <div className="dmip-showcase-text">
              <h2>Every Answer Builds the Graph</h2>
              <p>
                As consultants capture discovery answers, DMIP automatically builds a live
                knowledge graph connecting systems, people, risks, integrations, and data
                domains. Cross-system dependencies surface without manual effort. The graph
                exists before a single plan is written.
              </p>
            </div>
          </div>

          {/* Row 2 — text left, image right */}
          <div className="dmip-showcase-row dmip-showcase-row--reverse">
            <div className="dmip-showcase-image">
              <img src="/dmip_plans.png" alt="DMIP Plan Generation" />
            </div>
            <div className="dmip-showcase-text">
              <h2>Plans Generated from Discovery</h2>
              <p>
                Migration Plans, Governance Plans, and Data Quality Plans are generated
                section by section from structured discovery data. Each plan is editable
                in the browser, commentable by the team, and exportable to Word.
                Version 1.0 on day one of engagement.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* ── AI Architecture ── */}
      <section className="dmip-ai-section">
        <div className="container">
          <h2 className="dmip-ai-heading">Built for Sovereign AI</h2>
          <p className="dmip-ai-subheading">
            Today powered by Anthropic. Tomorrow, a D-DMF sovereign engine hosted in
            Australian infrastructure.
          </p>
          <div className="dmip-ai-images">
            <div className="dmip-ai-image-block">
              <img src="/dmip_ai_current.png" alt="DMIP AI — Phase 1 Current" />
              <p className="dmip-ai-caption">Phase 1 — Current</p>
            </div>
            <div className="dmip-ai-image-block">
              <img src="/dmip_ai_future.png" alt="DMIP AI — Phase 2 Sovereign AI" />
              <p className="dmip-ai-caption">Phase 2 — Sovereign AI</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Partner Tiers ── */}
      <section className="dmip-tiers-section">
        <div className="container">
          <div className="dmip-tiers-header">
            <h2>Choose Your Scope. We Structure the Deal.</h2>
          </div>

          <div className="dmip-tier-cards">
            {/* Tier 1 — Vertical Specialist */}
            <div className="dmip-tier-card">
              <h3>Vertical Specialist</h3>
              <p className="dmip-tier-scope">Scope</p>
              <p className="dmip-tier-scope-detail">
                1 vertical + 1 region<br />
                e.g. US Healthcare, UK Local Government
              </p>
              <ul className="dmip-tier-features">
                <li>Annual licence + per engagement fee</li>
                <li>Sovereign AI included</li>
              </ul>
            </div>

            {/* Tier 2 — Regional Partner */}
            <div className="dmip-tier-card">
              <h3>Regional Partner</h3>
              <p className="dmip-tier-scope">Scope</p>
              <p className="dmip-tier-scope-detail">
                All verticals + 1 region<br />
                e.g. All of Australia, All of US
              </p>
              <ul className="dmip-tier-features">
                <li>Annual licence + per engagement fee</li>
                <li>Sovereign AI included</li>
              </ul>
            </div>

            {/* Tier 3 — Master Partner */}
            <div className="dmip-tier-card dmip-tier-card--featured">
              <span className="dmip-tier-badge">Premium</span>
              <h3>Master Partner</h3>
              <p className="dmip-tier-scope">Scope</p>
              <p className="dmip-tier-scope-detail">
                All verticals + all regions
              </p>
              <ul className="dmip-tier-features">
                <li>Premium annual licence</li>
                <li>Volume engagement discount</li>
                <li>Sovereign AI included</li>
                <li>Global exclusivity rights</li>
              </ul>
            </div>
          </div>

          <div className="dmip-tiers-contact">
            <a href="mailto:krupesh@dmip.app?subject=DMIP Partnership">
              DM to discuss partnership terms
            </a>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="dmip-footer">
        <p>© 2026 Data Management Intelligence Platform. All rights reserved.</p>
        <p>By <a href="https://www.linkedin.com/in/krupeshdesai/" target="_blank" rel="noreferrer">Krupesh Desai</a></p>
        <p><a href="/privacy-policy">Privacy Policy</a></p>
      </footer>

    </div>
  );
};

export default DMIPPage;
